/**
 * Achievement starter catalogue (Plan 02-01 Task 2 / D-09 / ACH-05).
 *
 * Catalogue lives in code (not the DB). Each entry is a typed
 * `AchievementDef` whose `evaluate(ctx)` is a pure boolean over a snapshot
 * reader. The runtime service (apps/api/src/services/achievements.service.ts)
 * iterates the registry on every triggering event, calls `evaluate(ctx)`, and
 * idempotently INSERTs into the `achievements` table on the
 * `UNIQUE(member_id, key)` anchor. Adding a 13th achievement is JUST a new
 * entry here — no schema change (ACH-05 satisfied).
 *
 * Design principles:
 *  - **Pure evaluators**: same `(memberId, payload, snapshot)` → same boolean,
 *    every time, no side effects. Service layer wraps in idempotent SQL so
 *    duplicate emissions never double-grant.
 *  - **Snapshot reader**: `ctx.read.*` is the SQL-projection seam. Service
 *    constructs it once per event evaluation; the registry doesn't reach for
 *    `db` directly — keeps the catalogue testable from `@stell/shared`.
 *  - **Trigger filter**: `triggers` lists which event names the entry cares
 *    about. The service skips entries whose triggers don't include the
 *    incoming event — the achievement gallery is UI-only.
 *  - **Secret slots**: night_owl, early_bird, comeback render as fully
 *    blank silhouettes in the gallery until unlocked (FEATURES "secret ones").
 *
 * Milestone semantics (`chore_5`, `chore_25`, `chore_100`, `combo_3`):
 * intentional `===` (not `>=`). The achievement fires the exact moment the
 * member crosses the threshold. After that, the UNIQUE(member_id, key)
 * constraint prevents re-firing — but if the count grows further, the
 * evaluator returns false anyway, so the system is safe even without the
 * UNIQUE guard. Streak achievements use `>=` because the streak number is
 * bounded by the day-boundary cron and can plateau across multiple
 * `streak.updated` events.
 */

/** Snapshot reader supplied by the runtime — pure inputs to evaluators. */
export type AchievementCtx = {
  memberId: string;
  event: { name: string; payload: unknown };
  read: {
    /** Total approved chores ever for `memberId` (POINT-01 ledger join). */
    countApprovedChoresFor(memberId: string): number;
    /** Approved chores today in Europe/Oslo (D-13 day boundary). */
    todayApprovedCountOslo(memberId: string): number;
    /** Whole calendar days since member's most recent approval (Inf if never). */
    daysSinceLastApproval(memberId: string): number;
    /** Hour 0..23 in Europe/Oslo at "now". */
    osloHour(): number;
  };
};

/** Trigger event names — keep in lockstep with eventSchemas keys (D-14). */
export type AchievementTrigger =
  | 'chore.approved'
  | 'streak.updated'
  | 'point.earned'
  | 'weekly.closed';

/**
 * Terskelen et merke låses opp ved (#156, steg 6c). Den er eneste kilde for
 * tallet: evaluatoren leser den, og Merker viser den i skiven og i målet
 * («25 oppgaver», «3 på rad», «3 på én dag»). Merker uten en tellbar terskel
 * (Ukens regent, de hemmelige) har ingen.
 *
 *   chores   godkjente oppgaver totalt (`=== count`)
 *   streak   dager på rad (`>= count`)
 *   sameDay  godkjente oppgaver samme dag i Oslo (`=== count`)
 */
export type AchievementGoal = {
  kind: 'chores' | 'streak' | 'sameDay';
  count: number;
};

export type AchievementDef = {
  title_key: string;
  icon_key: string;
  secret: boolean;
  triggers: ReadonlyArray<AchievementTrigger>;
  goal?: AchievementGoal;
  evaluate: (ctx: AchievementCtx) => boolean;
};

/** Et merke for N godkjente oppgaver totalt; terskelen står bare i `goal`. */
function choreMilestone(key: string, icon_key: string, count: number): AchievementDef {
  return {
    title_key: `achievement.${key}.title`,
    icon_key,
    secret: false,
    triggers: ['chore.approved'],
    goal: { kind: 'chores', count },
    evaluate: (c) => c.read.countApprovedChoresFor(c.memberId) === count,
  };
}

/** Et merke for N godkjente oppgaver samme dag (Oslo); terskelen står bare i `goal`. */
function sameDayMilestone(key: string, count: number): AchievementDef {
  return {
    title_key: `achievement.${key}.title`,
    icon_key: 'check',
    secret: false,
    triggers: ['chore.approved'],
    goal: { kind: 'sameDay', count },
    evaluate: (c) => c.read.todayApprovedCountOslo(c.memberId) === count,
  };
}

/** Et merke for N dager på rad; terskelen står bare i `goal`. */
function streakMilestone(key: string, count: number): AchievementDef {
  return {
    title_key: `achievement.${key}.title`,
    icon_key: 'flame',
    secret: false,
    triggers: ['streak.updated'],
    goal: { kind: 'streak', count },
    evaluate: (c) => (c.event.payload as { currentStreak: number }).currentStreak >= count,
  };
}

/**
 * Twelve v1 achievement keys (D-09, ACH-05).
 *
 * Typed as a literal object (NOT `Record<string, …>`) so consumers get
 * non-optional access for known keys under noUncheckedIndexedAccess. The
 * `satisfies` clause guarantees every entry conforms to AchievementDef
 * without widening the inferred key set.
 */
export const ACHIEVEMENTS = {
  first_chore: choreMilestone('first_chore', 'sparkle', 1),
  chore_5: choreMilestone('chore_5', 'check', 5),
  chore_25: choreMilestone('chore_25', 'check', 25),
  chore_100: choreMilestone('chore_100', 'trophy', 100),
  streak_3: streakMilestone('streak_3', 3),
  streak_7: streakMilestone('streak_7', 7),
  streak_30: streakMilestone('streak_30', 30),
  weekly_winner: {
    title_key: 'achievement.weekly_winner.title',
    icon_key: 'crown',
    secret: false,
    triggers: ['weekly.closed'],
    evaluate: (c) =>
      (c.event.payload as { winnerMemberId: string | null }).winnerMemberId === c.memberId,
  },
  night_owl: {
    title_key: 'achievement.night_owl.title',
    icon_key: 'sparkle',
    secret: true,
    triggers: ['chore.approved'],
    evaluate: (c) => {
      const h = c.read.osloHour();
      return h >= 21 || h < 5;
    },
  },
  early_bird: {
    title_key: 'achievement.early_bird.title',
    icon_key: 'sparkle',
    secret: true,
    triggers: ['chore.approved'],
    evaluate: (c) => {
      const h = c.read.osloHour();
      return h >= 5 && h < 7;
    },
  },
  comeback: {
    title_key: 'achievement.comeback.title',
    icon_key: 'sparkle',
    secret: true,
    triggers: ['chore.approved'],
    evaluate: (c) => c.read.daysSinceLastApproval(c.memberId) >= 7,
  },
  combo_3: sameDayMilestone('combo_3', 3),
} satisfies Record<string, AchievementDef>;

/** Compile-time-known set of achievement keys. */
export type AchievementKey = keyof typeof ACHIEVEMENTS;

/** Stable, sorted list of all achievement keys — useful for tests + UI. */
export const ACHIEVEMENT_KEYS: readonly AchievementKey[] = (
  Object.keys(ACHIEVEMENTS) as AchievementKey[]
).sort();

/** Terskelen for et merke, eller `undefined` når det ikke har en tellbar terskel. */
export function achievementGoal(key: string): AchievementGoal | undefined {
  return (ACHIEVEMENTS as Record<string, AchievementDef | undefined>)[key]?.goal;
}
